import { useState } from 'react';
import PhaseSection from './PhaseSection';

interface Step {
  id: string;
  name: string;
  description: string;
  status: 'not-started' | 'in-progress' | 'completed' | 'delayed' | 'needs-update';
  dueDate?: string;
}

interface Phase {
  id: string;
  name: string;
  description: string;
  status: 'not-started' | 'in-progress' | 'completed' | 'delayed' | 'needs-update';
  steps: Step[];
}

const phases: Phase[] = [
  {
    id: 'phase-1',
    name: 'Phase 1: Plan',
    description: 'Initial planning and team organization phase',
    status: 'in-progress',
    steps: [
      {
        id: 'step-1-1',
        name: 'Identify Project Team Members',
        description: 'Identify and document project team members, key stakeholders, and leaders for CCET, CCMT, CCRT, and CSIRT',
        status: 'in-progress',
        dueDate: '2024-01-31'
      },
      {
        id: 'step-1-2',
        name: 'Develop Presentation',
        description: 'Develop a short (5-8 slide) presentation outlining CCMP development and activities for project kickoff',
        status: 'not-started',
        dueDate: '2024-02-07'
      },
      {
        id: 'step-1-3',
        name: 'Conduct Project Kickoff',
        description: 'Conduct project kickoff meeting(s) for the organization',
        status: 'not-started',
        dueDate: '2024-02-14'
      },
      {
        id: 'step-1-4',
        name: 'Schedule Stakeholder Meetings',
        description: 'Schedule meetings with key stakeholders from CCMT, CSIRT, and CCRT groups',
        status: 'not-started',
        dueDate: '2024-02-21'
      }
    ]
  },
  {
    id: 'phase-2',
    name: 'Phase 2: Build',
    description: 'Development and implementation of core components',
    status: 'not-started',
    steps: [
      {
        id: 'step-2-1',
        name: 'Execute Meetings',
        description: 'Execute meetings and deliver presentation brief, establish contacts, and set next meeting dates',
        status: 'not-started',
        dueDate: '2024-03-01'
      },
      {
        id: 'step-2-2',
        name: 'Distribute Plans',
        description: 'Send soft copies of detailed incident response plan and calendar invites for follow-up meetings',
        status: 'not-started',
        dueDate: '2024-03-08'
      },
      {
        id: 'step-2-3',
        name: 'Analyze Plans',
        description: 'Collect and analyze existing incident response plans, provide feedback within 1-2 days',
        status: 'not-started',
        dueDate: '2024-03-15'
      },
      {
        id: 'step-2-4',
        name: 'Document Current State',
        description: 'Document current state of cyber incident response and organizational crisis management',
        status: 'not-started',
        dueDate: '2024-03-22'
      },
      {
        id: 'step-2-5',
        name: 'Analyze Gaps',
        description: 'Check for functional overlaps and gaps between current and ideal state',
        status: 'not-started',
        dueDate: '2024-03-29'
      },
      {
        id: 'step-2-6',
        name: 'Develop Templates',
        description: 'Develop CCMP template and Quick Reference Cards (QRC)',
        status: 'not-started',
        dueDate: '2024-04-05'
      },
      {
        id: 'step-2-7',
        name: 'Update Plans',
        description: 'Update CCMP with draft functional incident response plans and QRC',
        status: 'not-started',
        dueDate: '2024-04-12'
      },
      {
        id: 'step-2-8',
        name: 'Refine Plans',
        description: 'Work with groups to update and refine their plans until initial baseline',
        status: 'not-started',
        dueDate: '2024-04-19'
      },
      {
        id: 'step-2-9',
        name: 'Submit for Approval',
        description: 'Submit baseline and QRC to SEIC, EIC, and project sponsor for approval',
        status: 'not-started',
        dueDate: '2024-04-26'
      }
    ]
  },
  {
    id: 'phase-3',
    name: 'Phase 3: Test',
    description: 'Testing and validation of implemented solutions',
    status: 'not-started',
    steps: [
      {
        id: 'step-3-1',
        name: 'Deliver Training',
        description: 'Schedule and deliver CCMP training to all members of CCMT, CCRT, and CSIRT',
        status: 'not-started',
        dueDate: '2024-05-10'
      },
      {
        id: 'step-3-2',
        name: 'Plan Exercises',
        description: 'Plan two initial tabletop exercises',
        status: 'not-started',
        dueDate: '2024-05-24'
      },
      {
        id: 'step-3-3',
        name: 'Develop Materials',
        description: 'Develop materials for the two initial tabletop exercises',
        status: 'not-started',
        dueDate: '2024-06-07'
      },
      {
        id: 'step-3-4',
        name: 'Conduct Exercises',
        description: 'Conduct the two initial tabletop exercises',
        status: 'not-started',
        dueDate: '2024-06-21'
      },
      {
        id: 'step-3-5',
        name: 'Create Reports',
        description: 'Develop After-Action Report for each cyber war game tabletop exercise',
        status: 'not-started',
        dueDate: '2024-06-28'
      },
      {
        id: 'step-3-6',
        name: 'Update CCMP',
        description: 'Update CCMP based on After-Action Report and subsequent functional IR Plan changes',
        status: 'not-started',
        dueDate: '2024-07-05'
      }
    ]
  },
  {
    id: 'phase-4',
    name: 'Phase 4: Implement',
    description: 'Final implementation and deployment',
    status: 'not-started',
    steps: [
      {
        id: 'step-4-1',
        name: 'Stakeholder Review',
        description: 'Review updated CCMP with key stakeholders to ensure alignment and completeness',
        status: 'not-started',
        dueDate: '2024-07-12'
      },
      {
        id: 'step-4-2',
        name: 'Feedback Implementation',
        description: 'Update CCMP based on stakeholders\' feedback and recommendations',
        status: 'not-started',
        dueDate: '2024-07-19'
      },
      {
        id: 'step-4-3',
        name: 'Final Review',
        description: 'Conduct final review of CCMP with stakeholders to validate all updates and changes',
        status: 'not-started',
        dueDate: '2024-07-26'
      },
      {
        id: 'step-4-4',
        name: 'CCMP Distribution',
        description: 'Distribute final CCMP to all stakeholders and establish in physical and virtual War Rooms',
        status: 'not-started',
        dueDate: '2024-08-02'
      },
      {
        id: 'step-4-5',
        name: 'Project Closure',
        description: 'Transition ownership of CCMP to designated owner and formally close the project',
        status: 'not-started',
        dueDate: '2024-08-09'
      }
    ]
  }
];

export default function ProjectPhases() {
  const [expandedPhases, setExpandedPhases] = useState<string[]>(['phase-1']);

  const togglePhase = (phaseId: string) => {
    setExpandedPhases(prev =>
      prev.includes(phaseId)
        ? prev.filter(id => id !== phaseId)
        : [...prev, phaseId]
    );
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow">
      <div className="space-y-8">
        {phases.map((phase) => (
          <PhaseSection
            key={phase.id}
            {...phase}
            isExpanded={expandedPhases.includes(phase.id)}
            onToggle={() => togglePhase(phase.id)}
          />
        ))}
      </div>
    </div>
  );
}